import { Dimensions, Platform, View } from 'react-native';
import React, { useEffect, useMemo, useState } from 'react';
import { SceneMap, TabBar, TabView } from 'react-native-tab-view';

import { MenuValue } from '@stores/menus/types';
import { Typography } from '@components/Typography';
import { crashlyticsUtil } from '@lib/crashlytics';
import { useMenusStore } from '@stores/menus';
import { useTranslation } from '@providers/I18n';
import { makeStyles, useTheme } from '@providers/Theme';

import { MenuContent } from './MenuContent';

type MenusTabsProps = {
  tabs: MenuValue[];
  selectedBranchUUID: string;
};

const useStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
  },
  scene: {
    width: '100%',
    backgroundColor: theme.palette.base[100],
    flex: 1,
  },
  sceneContainer: {
    backgroundColor: theme.palette.base[100],
  },
  tabBar: {
    borderBottomColor: theme.palette.grey[300],
    borderBottomWidth: 1,
    backgroundColor: theme.palette.base[100],
  },
  tab: {
    flexDirection: 'row',
  },
  activeTab: {
    width: 'auto',
  },
  tabLabel: {
    color: theme.palette.grey[700],
  },
  activeTabLabel: {
    color: theme.palette.primary[600],
  },
  indicatorContainerStyle: {
    left: 'auto',
    right: 0,
  },
}));

export const MenusTabs: React.FC<MenusTabsProps> = ({
  tabs,
  selectedBranchUUID,
}) => {
  const styles = useStyles();
  const theme = useTheme();
  const { i18n } = useTranslation();
  const [index, setIndex] = useState(0);
  const { fetchMenuDetails } = useMenusStore();

  const MenuView = () => (
    <View style={[styles.scene]}>
      <MenuContent />
    </View>
  );

  const routes = useMemo(() => {
    return tabs.map((tab) => {
      return {
        key: tab.uuid,
        title: i18n.language === 'ar' ? tab.name.ar : tab.name.en,
      };
    });
  }, [i18n.language, tabs]);

  const scenes = useMemo(() => {
    const x = tabs.map((tab) => {
      return { [tab.uuid]: MenuView };
    });
    return x.reduce((r, c) => Object.assign(r, c), {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs]);

  const tabChange = async (i: number) => {
    if (!tabs[i]) {
      crashlyticsUtil.log(`Invalid tab index=${i}`);
      return;
    }
    setIndex(i);
    await fetchMenuDetails(selectedBranchUUID, tabs[i].uuid);
  };

  useEffect(() => {
    crashlyticsUtil.log(
      `MenusTabs mounted routes=${routes.length}`
    );

    crashlyticsUtil.setAttribute('screen_component', 'MenusTabs');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <View style={{ height: '100%' }}>
      <TabView
        sceneContainerStyle={styles.sceneContainer}
        navigationState={{ index, routes }}
        renderScene={SceneMap(scenes)}
        renderTabBar={(props) => (
          <TabBar
            {...props}
            indicatorStyle={{ backgroundColor: theme.palette.primary[600] }}
            indicatorContainerStyle={
              Platform.OS === 'web' &&
              i18n.language === 'ar' &&
              styles.indicatorContainerStyle
            }
            // contentContainerStyle={}
            style={[styles.tabBar, ...(Platform.OS === 'web' ? [styles.tab] : [])]}
            tabStyle={Platform.OS === 'web' ? styles.activeTab : undefined} // here
            renderLabel={({ route, focused }) => (
              <Typography
                weight="medium"
                size={{ xs: 'md', md: 'xl' }}
                style={focused ? styles.activeTabLabel : styles.tabLabel}>
                {route.title}
              </Typography>
            )}
          />
        )}
        onIndexChange={tabChange}
        style={styles.container}
        initialLayout={{
          width: Platform.OS === 'web' ? 500 : Dimensions.get('window').width,
        }}
      />
    </View>
  );
};
